import { useMemo, useState } from 'react';
import {
  Box, Stack, Typography, TextField, Button, Chip,
  InputAdornment, Alert,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import PlayArrowOutlinedIcon from '@mui/icons-material/PlayArrowOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import ProzoDataGrid from '../../../common/ProzoDataGrid';
import { useGetSearchMerchantQuery } from '../../../redux/commonApiCalls';
import { resetWizard } from '../../../redux/pricingWizardSlice';

const KYC_FILTER = {
  andfilter: [
    { field: 'isTestMerchant', operator: 'ne', value: true },
    { field: 'status', operator: 'in', value: ['KYC_PENDING', 'KYC_UPLOADED'] },
  ],
  offset: 0,
  limit: 500,
  includes: 'id,name,companyName,merchantId,email,contact_no,city,status,walletType,isActive,kycDetails,merchantCategory',
};

const STATUS_CONFIG = {
  KYC_PENDING:  { label: 'KYC Pending',   color: 'warning' },
  KYC_UPLOADED: { label: 'Docs Uploaded', color: 'info' },
  KYC_APPROVED: { label: 'KYC Approved',  color: 'success' },
};


function fmtDate(dateStr) {
  if (!dateStr) return null;
  try { return new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }); }
  catch { return null; }
}

function mapRow(m) {
  const docs = m.kycDetails || [];
  const uploaded = docs.filter((d) => d.docUrl || d.uploadDate).length;
  const approved = docs.filter((d) => d.verified).length;
  const rejected = docs.filter((d) => d.rejected).length;
  const lastUpload = docs
    .filter((d) => d.uploadDate)
    .reduce((latest, d) => (!latest || d.uploadDate > latest) ? d.uploadDate : latest, null);
  const cat = m.merchantCategory || '';
  const tier = ['MEDIUM', 'LARGE', 'ENTERPRISE'].includes(cat) ? 'Premium'
    : ['MICRO', 'SMALL', 'SME'].includes(cat) ? 'Standard'
    : cat || null;

  return {
    id: m.id || m._id,
    merchantId: m.merchantId,
    name: m.name || '—',
    companyName: m.companyName || m.name || '—',
    email: m.email || '—',
    contact: m.contact_no || '—',
    city: m.city || '—',
    walletType: m.walletType || '—',
    isActive: m.isActive,
    status: m.status || 'KYC_PENDING',
    tier,
    kycUploaded: uploaded,
    kycApproved: approved,
    kycRejected: rejected,
    kycTotal: docs.length,
    lastUploadDate: fmtDate(lastUpload),
  };
}

export default function MerchantListing() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(15);

  // Always refetch on mount — the merchant list is created/updated through a
  // separate API slice that can't invalidate this cache, and the 1-hour
  // keepUnusedDataFor would otherwise show a stale queue that omits merchants
  // onboarded after the queue was first opened.
  const { data: apiData, isLoading, isError, refetch } = useGetSearchMerchantQuery(KYC_FILTER, {
    refetchOnMountOrArgChange: true,
  });

  const rows = useMemo(() => {
    const list = Array.isArray(apiData) ? apiData : Array.isArray(apiData?.data) ? apiData.data : [];
    return list.map(mapRow);
  }, [apiData]);

  const filtered = useMemo(() => {
    if (!query) return rows;
    const q = query.toLowerCase();
    return rows.filter((r) =>
      r.id.toLowerCase().includes(q) ||
      r.name.toLowerCase().includes(q) ||
      r.companyName.toLowerCase().includes(q) ||
      r.email.toLowerCase().includes(q) ||
      String(r.merchantId || '').includes(q)
    );
  }, [rows, query]);

  const columns = [
    {
      field: 'name',
      headerName: 'Merchant',
      minWidth: 250,
      flex: 1,
      sortable: false,
      renderCell: ({ row }) => (
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.25, wordBreak: 'break-word' }}>
            {row.companyName}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.disabled', fontFamily: 'JetBrains Mono, monospace', fontSize: 10, wordBreak: 'break-all' }}>
            {row.email !== '—' ? row.email : row.id}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'tier',
      headerName: 'Tier',
      minWidth: 150,
      sortable: false,
      renderCell: ({ row }) => row.tier ? (
        <Chip
          size="small"
          label={row.tier}
          sx={{
            height: 22, fontSize: 11, fontWeight: 700,
            ...(row.tier === 'Premium' ? {
              bgcolor: (t) => alpha(t.palette.warning.main, 0.15),
              color: 'warning.dark',
              border: (t) => `1px solid ${alpha(t.palette.warning.main, 0.4)}`,
            } : {
              bgcolor: (t) => alpha(t.palette.grey[500], 0.12),
              color: 'text.secondary',
              border: '1px solid',
              borderColor: 'divider',
            }),
          }}
        />
      ) : <Typography variant="body2" sx={{ color: 'text.disabled' }}>—</Typography>,
    },
    {
      field: 'status',
      headerName: 'Status',
      minWidth: 160,
      sortable: false,
      renderCell: ({ row }) => {
        const sc = STATUS_CONFIG[row.status] || STATUS_CONFIG.KYC_PENDING;
        return (
          <Chip
            size="small"
            label={sc.label}
            color={sc.color}
            sx={{ fontWeight: 700, fontSize: 11, height: 22 }}
          />
        );
      },
    },
    {
      field: 'kycUploaded',
      headerName: 'KYC Documents',
      minWidth: 200,
      sortable: false,
      renderCell: ({ row }) => (
        <Box>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Typography variant="body2" sx={{ fontWeight: 700, color: 'info.dark' }}>{row.kycUploaded}</Typography>
            <Typography variant="caption" sx={{ color: 'text.disabled' }}>/</Typography>
            <Typography variant="body2" sx={{ fontWeight: 700, color: row.kycApproved > 0 ? 'success.dark' : 'text.disabled' }}>{row.kycApproved}</Typography>
            <Typography variant="caption" sx={{ color: 'text.disabled' }}>/</Typography>
            <Typography variant="body2" sx={{ fontWeight: 700, color: row.kycRejected > 0 ? 'error.dark' : 'text.disabled' }}>{row.kycRejected}</Typography>
          </Stack>
        </Box>
      ),
    },
    {
      field: 'lastUploadDate',
      headerName: 'Last Upload',
      minWidth: 150,
      sortable: false,
      renderCell: ({ row }) => (
        <Typography variant="body2" sx={{ color: row.lastUploadDate ? 'text.primary' : 'text.disabled', fontWeight: row.lastUploadDate ? 600 : 400 }}>
          {row.lastUploadDate || '—'}
        </Typography>
      ),
    },
    {
      field: 'actions',
      headerName: '',
      minWidth: 160,
      sortable: false,
      align: 'right',
      headerAlign: 'right',
      renderCell: ({ row }) => (
        <Stack direction="row" spacing={0.75} justifyContent="flex-end">
          <Button
            variant="contained"
            size="small"
            disableElevation
            startIcon={<VisibilityOutlinedIcon sx={{ fontSize: '14px !important' }} />}
            onClick={() => navigate(`/merchant-config/merchant/${row.id}`)}
            sx={{ textTransform: 'none', fontWeight: 600, fontSize: 11, px: 1.25, py: 0.4, minWidth: 0, borderRadius: 1.5 }}
          >
            View
          </Button>
          <Button
            variant="contained"
            size="small"
            disableElevation
            color="success"
            startIcon={<PlayArrowOutlinedIcon sx={{ fontSize: '14px !important' }} />}
            onClick={() => { dispatch(resetWizard()); navigate(`/merchant-config/pricing-setup?merchantId=${row.id}`); }}
            sx={{ textTransform: 'none', fontWeight: 600, fontSize: 11, px: 1.25, py: 0.4, minWidth: 0, borderRadius: 1.5 }}
          >
            Setup
          </Button>
        </Stack>
      ),
    },
  ];

  return (
    <Box>

      {isError && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} action={<Button size="small" onClick={refetch}>Retry</Button>}>
          Failed to load merchants. Please retry.
        </Alert>
      )}

      {/* Grid */}
      <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, overflow: 'hidden', bgcolor: 'background.paper' }}>
        {/* Table header toolbar */}
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          flexWrap="wrap"
          rowGap={1}
          sx={{ px: 2.5, py: 1.5, borderBottom: 1, borderColor: 'divider' }}
        >
          <TextField
            size="small"
            placeholder="Search by name, company, email or merchant ID…"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setPage(0); }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 17, color: 'text.disabled' }} /></InputAdornment> }}
            sx={{ minWidth: 320, '& .MuiOutlinedInput-root': { borderRadius: 20, bgcolor: 'grey.50' } }}
          />
          <Box sx={{ flex: 1 }} />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => { dispatch(resetWizard()); navigate('/merchant-config/pricing-setup'); }}
            disableElevation
            sx={{ fontWeight: 700, borderRadius: 2, textTransform: 'none' }}
          >
            New Merchant
          </Button>
        </Stack>
        <ProzoDataGrid
          rows={filtered}
          columns={columns}
          getRowId={(r) => r.id}
          loading={isLoading}
          page={page}
          setPage={setPage}
          sizePerPage={pageSize}
          setSizePerPage={setPageSize}
          rowCount={filtered.length}
          paginationMode="client"
          getRowHeight={() => 'auto'}
          flat
        />
      </Box>
    </Box>
  );
}
